'use client';

import { useTranslation } from '@/lib/react/i18n';
import { isHttps } from '@/lib/utils';
import { GroupType, GroupTypeValue } from '@/restapi/group';
import Image from 'next/image';
import React from 'react';
import { PiLockKeyFill } from "react-icons/pi";
import { BiSolidTrophy } from "react-icons/bi";
import { MdOutlineApproval } from "react-icons/md";

type IconType = 'User' | 'Group';

export type IconSizeProps = {
	width: number;
	height: number;
};

interface UserIconProps {
	fileURL?: string;
	plusDisabledIcon?: boolean;
	size?: IconSizeProps;
	className?: string;
	iconType?: IconType;
	count?: number;
	borderEnable?: boolean;
	borderColor?: string;
	groupType?: GroupType; 
	iconSize?: number;
	publicBoderSize?: number;
}

const ImageIconComponent: React.FC<UserIconProps> = ({
	fileURL,
	plusDisabledIcon = true,
	className,
	size = { width: 50, height: 50 },
	iconType = 'User',
	count = 0,
	groupType,
	iconSize = 6,
	publicBoderSize = 2,
}) => {
	const { t } = useTranslation();

	// +マークのスタイル
	const plusStyles: React.CSSProperties = {
		position: 'absolute',
		bottom: '2px',
		right: '2px',
		width: size.width / 4,
		height: size.height / 4,
		border: '2px solid #0000ff',
		borderRadius: '50%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		fontSize: size.width / 4,
		color: '#fff',
		backgroundColor: '#0000ff',
	};

	// バッジのスタイル
	const countStyles: React.CSSProperties = {
		position: 'absolute',
		bottom: '2px',
		right: '2px',
		width: size.width / 3,
		height: size.height / 3,
		borderRadius: '50%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		fontSize: size.width / 5,
		color: 'red',
	};

	// アイコン本体 + 装飾をまとめた baseIcon
	const baseIcon = (
		<div
			className={className}
			style={{
				position: 'relative',
				width: size.width,
				height: size.height,
				backgroundColor: '#f3f4f6',
				borderRadius: '50%',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				fontSize: size.width / 6,
				color: '#000',
			}}
		>
			{fileURL ? (
				isHttps(fileURL) ? (
					<Image
						className="object-cover"
						src={fileURL}
						alt=""
						width={size.width}
						height={size.height}
						style={{ borderRadius: '50%', width: '100%', height: '100%' }}
						priority
					/>
				) : (
					// eslint-disable-next-line @next/next/no-img-element
					<img
						className="object-cover"
						src={fileURL}
						alt=""
						width={size.width}
						height={size.height}
						style={{ borderRadius: '50%', width: '100%', height: '100%' }}
					/>
				)
			) : iconType === 'User' ? (
				<span>
					{t('User')}
					<br />
					{t('Icon')}
				</span>
			) : (
				<span>
					{t('Group')}
					<br />
					{t('Icon')}
				</span>
			)}

			{/* +マーク */}
			{!plusDisabledIcon && <div style={plusStyles}>+</div>}

			{/* カウントバッジ */}
			{count > 0 && <div className="bg-red-600 border-red-600 p-1" style={countStyles}>{count > 99 ? 99 : count}</div>}

			{/* 承認制 */}
			{groupType === GroupTypeValue.PUBLIC_PERMISSION  && (
				<div
					data-testid="group-icon-approval"
					className={`absolute bottom-0 right-0 bg-white text-black rounded-full p-[${iconSize}px] border-2 border-[rgb(0,0,255)]`}
					style={{ fontSize: size.width / 6 }}
				>
						<MdOutlineApproval />
				</div>
			)}

			{/* 鍵アイコン（クローズ） */}
			{groupType === GroupTypeValue.PRIVATE  && (
				<div
					data-testid="group-icon-lock"
					className={`absolute bottom-0 right-0 bg-white text-black rounded-full p-[${iconSize}px] border-2 border-[rgb(0,0,255)]`}
					style={{ fontSize: size.width / 6 }}
				>
						<PiLockKeyFill />
				</div>
			)}
			{groupType === GroupTypeValue.CONTEST  && (
				<div
					className={`absolute bottom-0 right-0 bg-white text-black rounded-full p-[${iconSize}px] border-2 border-[rgb(0,0,255)]`}
					style={{ fontSize: size.width / 6 }}
				>
						<BiSolidTrophy
						/>
				</div>
			)}
		</div>
	);

	// オープン（1）の場合：白線 2px を外側、グラデ線 2px を内側に配置
	// if (groupType === GroupTypeValue.PUBLIC) {
	// 	return (
	// 		<div
	// 			// まず外側に白線 2px
	// 			className={`p-[${publicBoderSize}px] bg-white rounded-full`}
	// 		>
	// 			<div
	// 				// その内側にグラデ線 2px
	// 				className={`p-[${publicBoderSize}px] rounded-full`}
	// 				style={{
	// 					background: 'linear-gradient(to top right, rgb(0,0,255), rgb(215,0,255), white)',
	// 				}}
	// 			>
	// 				{baseIcon}
	// 			</div>
	// 		</div>
	// 	);
	// }

	// 承認制（3）またはプライベート（2）はそのまま表示
	return baseIcon;
};

export default ImageIconComponent;
